import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MSAL_LOGIN_VALUES } from '@app/core/constants/app-constant';
import { ADD_FILE_OPTION_NAME } from '@app/core/constants/app.enum';
import { STORAGE_CONST } from '@app/core/constants/storage-constants';
import { Box } from '@app/core/providers/box';
import { Dropbox } from '@app/core/providers/dropbox';
import { Onedrive } from '@app/core/providers/onedrive';
import { GoogleService } from '@app/core/services/google/google.service';
import { IntuneService } from '@app/core/services/intune/intune.service';
import { SharedService } from '@app/core/services/shared/shared.service';
import { DropBoxPayload } from '@app/shared/models/DropBoxPayload';
import { LoginFor } from '@app/shared/models/User';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MsalService } from '@azure/msal-angular';
import { NavController } from '@ionic/angular';
import { StorageService } from '../storage/storage.service';
import { AccountsService } from './accounts.service';

@Injectable({
  providedIn: 'root',
})
export class AccountsStateService {
  LINK_DROPBOX = '/app/add-files/dropbox-list';
  LINK_BOX = '/app/add-files/box-list';
  LINK_ONE_DRIVE = '/app/add-files/onedrive-list';
  constructor(
    private accountsSvc: AccountsService,
    private googleSvc: GoogleService,
    private sharedSvc: SharedService,
    private navCtrl: NavController,
    private router: Router,
    private onedrive: Onedrive,
    private dropbox: Dropbox,
    private box: Box,
    private authService: MsalService,
    public intuneService: IntuneService,
    private storageSvc: StorageService
  ) {}

  init() {
    this.accountsSvc.accountStateChanged.subscribe((account) => {
      switch (account[0]) {
        case ADD_FILE_OPTION_NAME.Google_Drive: //"Google Drive":
          if (account[1]) {
            this.handleGoogleDrive();
          } else {
            this.accountsSvc.removeAccount(LoginFor.gDrive);
          }
          break;
        case ADD_FILE_OPTION_NAME.One_Drive: //'One Drive':
          if (account[1]) {
            this.onedriveAuth();
          } else {
            this.accountsSvc.removeAccount(STORAGE_CONST.ONE_DRIVE);
          }
          break;
        case ADD_FILE_OPTION_NAME.Box: //'Box':
          if (account[1]) {
            this.handleBoxFiles();
          } else {
            this.accountsSvc.removeAccount(STORAGE_CONST.BOX);
          }
          break;
        case ADD_FILE_OPTION_NAME.Drop_Box: //'Drop Box':
          if (account[1]) {
            this.dropboxAuth();
          } else {
            this.accountsSvc.removeAccount(STORAGE_CONST.DROP_BOX);
          }
          break;
        case ADD_FILE_OPTION_NAME.Office_365: //'Office 365':
          if (account[1]) {
            this.officeAuth();
          } else {
            this.accountsSvc.removeAccount(STORAGE_CONST.OFFICE_365);
          }
          break;
      }
    });
  }

  async handleGoogleDrive() {
    if (this.sharedSvc.isNavigateFromUpload) {
      return;
    }

    try {
      const account = await this.accountsSvc.getGoogleDriveAccount();
      const authentication = account?.user?.authentication;

      if (!authentication?.idToken) {
        this.googleSvc.signIn(LoginFor.gDrive);
        return;
      }

      let accessToken = authentication.accessToken;
      const helper = new JwtHelperService();

      if (helper.isTokenExpired(authentication.idToken)) {
        const refreshedUser = await this.googleSvc.refreshAccessToken(account.user);
        accessToken = refreshedUser.authentication.accessToken;
      } else {
        await this.googleSvc.checkToken(accessToken);
      }
    } catch (error) {
      this.googleSvc.signIn(LoginFor.gDrive);
    } finally {
      this.sharedSvc.isNavigateFromUpload = false;
    }
  }

  async handleBoxFiles() {
    const boxToken = await this.box.getToken();
    if (boxToken == null) {
      this.box.authorize().then(
        (success) => {
          this.navCtrl.navigateForward([this.LINK_BOX]);
          // const folders = this.dropbox.getFolders();
        },
        (err) => {}
      );
    } else {
      this.box.setAccessToken(boxToken);
      this.navCtrl.navigateForward([this.LINK_BOX]);
    }
  }

  async dropboxAuth() {
    // const dropboxToken = await this.dropbox.getToken();
    const dropBoxUser: DropBoxPayload = await this.dropbox.getUserFromStorage();
    if (dropBoxUser) {
      this.navCtrl.navigateForward([this.LINK_DROPBOX]);
    } else {
      this.dropbox.login();
    }
  }

  async officeAuth() {
    const onedriveAccount = await this.storageSvc.get(
      STORAGE_CONST.SERVICE_ACCOUNT_ID(STORAGE_CONST.OFFICE_365)
    );
    this.storageSvc.set(STORAGE_CONST.MSAL_LOGIN, MSAL_LOGIN_VALUES.office365);
    const accounts = this.authService.instance
      .getAllAccounts()
      .filter((a) => a.username === onedriveAccount);
    if (accounts.length > 0) {
      this.onedrive.setService(ADD_FILE_OPTION_NAME.Office_365); // "Office 365"
      this.router.navigate([this.LINK_ONE_DRIVE]);
    } else {
      this.sharedSvc.removeIntractionInProgressMsal();
      this.authService
        .loginRedirect({
          scopes: ['user.read', 'Files.Read.All', 'offline_access'],
          prompt: 'select_account',
        })
        .subscribe(() => {
          this.onedrive.setService(ADD_FILE_OPTION_NAME.Office_365); //'Office 365'
        });
    }
  }

  async onedriveAuth() {
    const onedriveAccount = await this.storageSvc.get(
      STORAGE_CONST.SERVICE_ACCOUNT_ID(STORAGE_CONST.ONE_DRIVE)
    );
    this.storageSvc.set(STORAGE_CONST.MSAL_LOGIN, MSAL_LOGIN_VALUES.onedrive);
    const accounts = this.authService.instance
      .getAllAccounts()
      .filter((a) => a.username === onedriveAccount);
    if (accounts.length > 0) {
      this.onedrive.setService(ADD_FILE_OPTION_NAME.One_Drive); //'One Drive'
      this.router.navigate([this.LINK_ONE_DRIVE]);
    } else {
      this.sharedSvc.removeIntractionInProgressMsal();

      this.authService
        .loginRedirect({
          scopes: ['user.read', 'Files.Read.All', 'offline_access'],
          prompt: 'select_account',
        })
        .subscribe(() => {
          this.onedrive.setService(ADD_FILE_OPTION_NAME.One_drive); //'One drive'
          // this.router.navigate([this.LINK_ONE_DRIVE]);
        });
    }
  }
}
